import BaseTableNew from 'components/atoms/base-table/new';
import {
  MEDICAL_HISTORY_PER_PAGE,
  useGetMedicalHistory,
  useUpdateMedicalHistory,
} from 'infrastructure/hooks/ccm';
import {
  type Dispatch,
  type SetStateAction,
  type FC,
  useState,
  useEffect,
} from 'react';
import BaseModal from 'components/atoms/base-modal';
import { t } from 'i18next';
import MedicalHistoryForm from 'components/molecules/admin-panel/forms/medical-history';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';

import { columns } from './columns';

import type { SortState } from 'infrastructure/types';
import type { Values } from 'components/molecules/admin-panel/forms/medical-history/validation-schema';
import type { IUpdateMedicalHistory } from 'infrastructure/interfaces';

interface MedicalHistoryTableProps {
  patientGuid: string;
  setPage: Dispatch<SetStateAction<number>>;
  page: number;
  disableActions?: boolean;
  refreshToken?: number;
}

type THistoryForUpdate = Omit<IUpdateMedicalHistory, 'patientGuid'>;

const MedicalHistoryTable: FC<MedicalHistoryTableProps> = ({
  patientGuid,
  page,
  setPage,
  disableActions,
  refreshToken,
}) => {
  const [open, setOpen] = useState(false);
  const [documentationTimeSort, setDocumentationTimeSort] =
    useState<SortState>('');
  const [updatedAtSort, setUpdatedAtSort] = useState<SortState>('');
  const [electronicallySignedBySort, setElectronicallySignedBySort] =
    useState<SortState>('');
  const [historyForUpdate, setHistoryForUpdate] = useState<THistoryForUpdate>();
  const { medicalHistory, isLoading, refetch } = useGetMedicalHistory({
    patientGuid,
    documentationTimeSort,
    updatedAtSort,
    electronicallySignedBySort,
    meta: { page },
  });
  const { updateMedicalHistory, updateLoading } = useUpdateMedicalHistory({
    page,
  });

  const toggleOpen = () => setOpen((prev) => !prev);

  const onSubmit = async ({ record }: Values) => {
    if (record && historyForUpdate)
      updateMedicalHistory(
        { ...historyForUpdate, record, patientGuid },
        {
          onSuccess: () => {
            setOpen(false);
            refetch();
          },
        },
      );
  };

  const onCancelForm = () => {
    setHistoryForUpdate(undefined);
    setOpen(false);
  };

  useEffect(() => {
    if (refreshToken === undefined) return;
    refetch();
  }, [refreshToken, refetch]);

  return (
    <>
      <BaseTableNew
        loading={isLoading}
        items={medicalHistory?.items ?? []}
        columns={columns({
          setHistoryForUpdate(v) {
            toggleOpen();
            setHistoryForUpdate(v);
          },
          documentationTimeSort,
          updatedAtSort,
          electronicallySignedBySort,
          loading: updateLoading,
          updatedItemGuid: historyForUpdate?.guid,
          disableActions,
        })}
        bordered
        changeOddRowColor={false}
        rowHoverable={false}
        pagination={{
          current: page,
          pageSize: MEDICAL_HISTORY_PER_PAGE,
          total: medicalHistory?.count ?? 0,
          position: ['bottomRight'],
        }}
        onChangePage={(newPage) => {
          setPage(newPage);
        }}
        dataCy="medicalHistory"
        emptyText={
          <EmptyTablePlaceholder
            text={t('labels.noMedicalHistoryRecordings')}
          />
        }
        onSort={(field, order) => {
          setUpdatedAtSort('');
          setDocumentationTimeSort('');
          setElectronicallySignedBySort('');
          if (order) {
            if (field === 'updatedAt') {
              setUpdatedAtSort(order);
            } else if (field === 'documentationTime') {
              setDocumentationTimeSort(order);
            } else if (field === 'author') {
              setElectronicallySignedBySort(order);
            }
          }
        }}
      />
      <BaseModal
        title={t('labels.editMedicalHistory')}
        open={open}
        onCancel={toggleOpen}
      >
        <MedicalHistoryForm
          loading={updateLoading}
          onCancelForm={onCancelForm}
          onSubmit={onSubmit}
          defaultValues={historyForUpdate}
        />
      </BaseModal>
    </>
  );
};
export default MedicalHistoryTable;
